/**
 * PostHog Event Taxonomy
 *
 * Centralized event name definitions for consistent analytics across
 * the Digital Aggregate platform. Used by both web and mobile clients.
 *
 * Naming convention: `{domain}_{action}` in snake_case.
 *
 * Event-shape convention (applies to every event defined here):
 * - Every resource-scoped event carries its primary `{resource}_id`
 *   (e.g. `sample_id`, `analysis_id`).
 * - Outcome events (e.g. `analysis_completed`, `analysis_failed`) carry
 *   both the resource's own id and its parent id where one exists.
 * - Status/outcome is a discriminating property on a single event name
 *   rather than split into per-status event names.
 * - No user-authored free-text fields (e.g. `sample_name`, notes) —
 *   these can carry client-sensitive context and violate the GDPR framing.
 *
 * All events automatically include super properties:
 * - `$environment` (local/staging/production/unknown) — set at runtime
 *   via `<EnvBootstrap />` + `<meta name="x-app-env">`
 * - `platform` (web/mobile_ios/mobile_android)
 * - `app_version`
 *
 * Constants are only added to this file when a corresponding call site
 * exists. Orphan constants make it look like dashboards have data that
 * they don't — add them when you wire the event, not before.
 */

// ============================================================================
// Auth Events
// ============================================================================
export const AUTH_LOGIN = "auth_login";
export const AUTH_LOGOUT = "auth_logout";

// ============================================================================
// Sample Events
// ============================================================================
export const SAMPLE_CREATED = "sample_created";
export const SAMPLE_UPDATED = "sample_updated";
export const SAMPLE_DELETED = "sample_deleted";

// ============================================================================
// Analysis Events
// ============================================================================
export const ANALYSIS_STARTED = "analysis_started";
export const ANALYSIS_COMPLETED = "analysis_completed";
export const ANALYSIS_FAILED = "analysis_failed";
export const ANALYSIS_CANCELLED = "analysis_cancelled";
export const ANALYSIS_ARCHIVED = "analysis_archived";
export const ANALYSIS_RESUMED = "analysis_resumed";
export const ANALYSIS_SIGNED_OFF = "analysis_signed_off";
export const ANALYSIS_UPDATED = "analysis_updated";

// ============================================================================
// Sample Lifecycle Events
// ============================================================================
export const SAMPLE_ARCHIVED = "sample_archived";

// ============================================================================
// Billing Events — Customer-Facing
// ============================================================================
export const BILLING_SUBSCRIPTION_VIEWED = "billing.subscription.viewed";
export const BILLING_PREVIEW_OPENED = "billing.preview.opened";
export const BILLING_CHECKOUT_STARTED = "billing.checkout.started";
export const BILLING_CHECKOUT_REDIRECTED = "billing.checkout.redirected";
export const BILLING_SUBSCRIPTION_ACTIVATED = "billing.subscription.activated";
export const BILLING_LOCATION_ADDED = "billing.location.added";
export const BILLING_LOCATION_REMOVED = "billing.location.removed";
export const BILLING_LOCATION_REACTIVATED = "billing.location.reactivated";
export const BILLING_PLAN_CHANGED = "billing.plan.changed";
export const BILLING_PLAN_PENDING_CANCELED = "billing.plan.pending_canceled";
export const BILLING_SUBSCRIPTION_CANCELED = "billing.subscription.canceled";
export const BILLING_PORTAL_OPENED = "billing.portal.opened";
export const BILLING_INVOICE_VIEWED = "billing.invoice.viewed";
export const BILLING_INFO_UPDATED = "billing.info.updated";
export const BILLING_ADDON_ATTACHED = "billing.addon.attached";
export const BILLING_ADDON_DETACHED = "billing.addon.detached";
export const BILLING_ADDON_CHECKOUT_STARTED = "billing.addon.checkout_started";

// ============================================================================
// Infrastructure / Error Events
// ============================================================================

/**
 * Server-side: emitted by `backendFetch` on backend 5xx responses.
 * Rate-limited by an in-memory token bucket per `endpoint|status`.
 */
export const API_ERROR = "api_error";

/**
 * Client-side: emitted by `logger.error()` for unhandled/reported errors on
 * the browser. Kept centralized here so dashboards filter on a constant, not
 * a string literal that can drift.
 */
export const LOG_ERROR = "log_error";

/**
 * Client-side: emitted by `bffFetch` for JSON BFF round-trips.
 * Also emitted by the decoded-masks octet-stream path (which cannot use
 * `bffFetch`) so existing BFF latency dashboards include mask fetches.
 */
export const BFF_LATENCY = "bff_latency";

/**
 * Server-side: a decoded-masks request failed, was aborted, or took longer
 * than 2s. Fast successful requests stay on the response timing headers.
 */
export const DECODED_MASKS_SERVER = "decoded_masks_server";

/**
 * Client-side: detailed latency for `/api/image-analysis/.../decoded-masks`.
 * Carries server `X-Timing-*` phase breakdown plus client total so we can
 * separate blob fetch / JSON parse / RLE decode / binary build from network
 * and client parse overhead. Fire-and-forget; never blocks mask loading.
 */
export const DECODED_MASKS_LATENCY = "decoded_masks_latency";

/**
 * Client-side: an image was selected but carries no mask URL, so no
 * decoded-masks request is made at all.
 *
 * This path is otherwise silent — it emits no `decoded_masks_latency`, no
 * `bff_latency` and no error — which previously made a missing mask
 * indistinguishable from a healthy load in every dashboard.
 */
export const MASK_UNAVAILABLE = "mask_unavailable";

/**
 * Client-side: a decoded-masks download acquired a slot and started.
 * Paired with `decoded_masks_latency`, which only fires once the attempt ends.
 */
export const MASK_FETCH_STARTED = "mask_fetch_started";

/**
 * Client-side: the image tab stayed open long enough that masks should have
 * painted, and they have not. Fired once per image after 45s of visible time.
 */
export const MASK_OVERLAY_STUCK = "mask_overlay_stuck";
